import { ActionReducerMapBuilder } from '@reduxjs/toolkit'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import set from 'lodash/set'

import {
    fetchDataRequest,
    fetchDataSuccess,
    fetchDataFailure,
    setDataLoading,
    updateSorter,
    downloadDataRequest,
    downloadDataSuccess,
    downloadDataFailure,
} from 'actions/ui/shared'
import {
    FieldPropertiesSerializable,
    Pagination,
    PeriodDeltaType,
    Sorter,
} from 'types'

import { SharedState } from './state'

interface DataState {
    loading: boolean
    error: string | null
    downloading: boolean
    data: any[]
    [att: string]: any
}

export interface TableState extends DataState {
    tableSettings: {
        columns: FieldPropertiesSerializable[]
        sorter: Sorter
        pagination: Pagination
        showPeriodDeltas: boolean
        periodDeltaType: PeriodDeltaType
        showTotalRow: boolean
        periodDeltaDateRange: string[]
    }
}

export interface ListState extends DataState {
    listSettings: {
        sorter: Sorter
    }
}

const isTableState = (state: DataState): state is TableState =>
    get(state, 'tableSettings') !== undefined

const getSettingsName = (state: SharedState, path: string[]): string =>
    isTableState(get(state, path)) ? 'tableSettings' : 'listSettings'

// Cases added to the shared/index createReducer; state is parent UI state
export const addDataManagementCases = (
    builder: ActionReducerMapBuilder<SharedState>
): void => {
    builder
        // Set Loading State Directly
        .addCase(setDataLoading, (state, action) => {
            const { path, data: loading } = action.payload
            set(state, [...path, 'loading'], loading)
        })

        // Data Fetching
        .addCase(fetchDataRequest, (state, action) => {
            const { path } = action.payload
            set(state, [...path, 'loading'], true)
        })
        .addCase(fetchDataSuccess, (state, action) => {
            const {
                path,
                data: { results, count, unrestricted_count },
            } = action.payload
            set(state, [...path, 'loading'], false)
            set(state, [...path, 'error'], null)
            set(state, [...path, 'data'], results)
            if (isTableState(get(state, path))) {
                if (!isNil(count)) {
                    set(
                        state,
                        [...path, 'tableSettings', 'pagination', 'total'],
                        count
                    )
                }
                if (!isNil(unrestricted_count)) {
                    set(
                        state,
                        [
                            ...path,
                            'tableSettings',
                            'pagination',
                            'unrestricted_total',
                        ],
                        unrestricted_count
                    )
                }
            }
        })
        .addCase(fetchDataFailure, (state, action) => {
            const {
                path,
                data: { message },
            } = action.payload
            set(state, [...path, 'loading'], false)
            set(state, [...path, 'error'], message)
        })

        // Sorter
        .addCase(updateSorter, (state, action) => {
            const { path, data: sorter } = action.payload
            const settingsName = getSettingsName(state, path as string[])
            if (isTableState(get(state, path))) {
                set(state, [...path, settingsName, 'pagination', 'current'], 1)
            }
            set(state, [...path, settingsName, 'sorter'], sorter)
        })

        // Downloads
        .addCase(downloadDataRequest, (state, action) => {
            const { path } = action.payload
            set(state, [...path, 'downloading'], true)
        })
        .addCase(downloadDataSuccess, (state, action) => {
            const { path } = action.payload
            set(state, [...path, 'downloading'], false)
        })
        .addCase(downloadDataFailure, (state, action) => {
            const {
                path,
                data: { message },
            } = action.payload
            set(state, [...path, 'downloading'], false)
            set(state, [...path, 'error'], message)
        })
}
